import {Component, DestroyRef, inject, OnDestroy, signal, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {FormsModule} from "@angular/forms"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {MatInputModule} from "@angular/material/input"
import {MatSnackBar} from "@angular/material/snack-bar"
import {MatTooltip} from "@angular/material/tooltip"
import {ActivatedRoute, Router} from "@angular/router"
import {ConfigMenuService} from "@app/common/components/viewers/configurator/config-menu/services/config-menu.service"
import {ConfiguratorComponent} from "@app/common/components/viewers/configurator/configurator/configurator.component"
import {
    MaterialExplorerItemFragment,
    MaterialExplorerMaterialRevisionFragment,
    MaterialExplorerTextureFragment,
    MaterialExplorerTextureRevisionFragment,
    MaterialExplorerTextureSetFragment,
    MaterialExplorerTextureSetRevisionFragment,
} from "@app/platform/components/materials/material-explorer/material-explorer.generated"
import {ThumbnailsService} from "@app/platform/services/thumbnails/thumbnails.service"
import type {CmLogger} from "@cm/log"
import {deserializeMaterialGraph, Mapping, MaterialGraph, TextureFromRevision, TextureSet} from "@cm/material-nodes"
import {IsDefined} from "@cm/utils/filter"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {LoadingSpinnerComponent} from "@common/components/progress"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {MeasurementTypes} from "@common/models/settings/settings"
import {CM_LOGGER_MATERIALS} from "@common/services/logging"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {DataObjectType, ImageDataType, TextureType} from "@generated"
import {UtilsService} from "@legacy/helpers/utils"
import {AddTextureDialogComponent} from "@platform/components/materials/add-texture-dialog/add-texture-dialog.component"
import {
    MaterialExplorerCreateTextureRevisionGQL,
    MaterialExplorerCreateTextureSetRevisionGQL,
    MaterialExplorerDataGQL,
    MaterialExplorerDeleteTextureRevisionGQL,
    MaterialExplorerDeleteTextureSetRevisionGQL,
    MaterialExplorerItemGQL,
} from "@platform/components/materials/material-explorer/material-explorer.generated"
import {AddTextureDialogResult} from "@platform/models/dialogs/add-texture-dialog-result"
import {TextureTypeLabels} from "@platform/models/state-labels/texture-type-labels"
import {filter, firstValueFrom, Observable, Subject, switchMap} from "rxjs"

export const fabricMaterialInputId = "combinedModels/material"

type OldMaterialLayoutData = {
    type: "OldMaterialLayout"
    texture: MaterialExplorerTextureFragment
    textureRevision?: MaterialExplorerTextureRevisionFragment
    texImageNode: TextureFromRevision
    mappingNode: Mapping
}

type NewMaterialLayoutData = {
    type: "NewMaterialLayout"
    textureSetRevision?: MaterialExplorerTextureSetRevisionFragment
    imageTextureSetNode: TextureSet
    mappingNode: Mapping
}

type MaterialLayoutData = OldMaterialLayoutData | NewMaterialLayoutData

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-explorer",
    templateUrl: "./material-explorer.component.html",
    styleUrls: ["./material-explorer.component.scss"],
    imports: [RoutedDialogComponent, LoadingSpinnerComponent, MatInputModule, MatTooltip, FormsModule, ConfiguratorComponent],
})
export class MaterialExplorerComponent implements OnDestroy {
    readonly $routedDialog = viewChild.required(RoutedDialogComponent)
    readonly $configurator = viewChild.required<ConfiguratorComponent>("configurator")
    dropActive = {}
    dialogSizes = DialogSize
    unsubscribe = new Subject<void>()
    measurementTypes = MeasurementTypes
    textureSet!: MaterialExplorerTextureSetFragment
    materialRevision!: MaterialExplorerMaterialRevisionFragment
    materialGraph!: MaterialGraph
    materialLayoutData!: MaterialLayoutData

    loading = false
    readonly $showSizeOptions = signal(false)

    readonly $parameterWidth = signal<number>(15)
    readonly $parameterHeight = signal<number>(15)
    readonly $parameterRotation = signal<number>(0)

    readonly destroyRef = inject(DestroyRef)
    readonly organizations = inject(OrganizationsService)
    readonly refresh = inject(RefreshService)
    configMenuService = inject(ConfigMenuService)
    private readonly logger: CmLogger = inject(CM_LOGGER_MATERIALS)

    organizationId?: string
    organizationLegacyId?: number
    readonly $organizationTemplateId = signal<string | undefined>(undefined)
    readonly $materialExplorerItem = signal<MaterialExplorerItemFragment | undefined>(undefined)

    readonly materialExplorerDataGql = inject(MaterialExplorerDataGQL)
    readonly itemGql = inject(MaterialExplorerItemGQL)
    readonly deleteTextureRevisionGql = inject(MaterialExplorerDeleteTextureRevisionGQL)
    readonly deleteTextureSetRevisionGql = inject(MaterialExplorerDeleteTextureSetRevisionGQL)
    readonly createTextureRevisionGql = inject(MaterialExplorerCreateTextureRevisionGQL)
    readonly createTextureSetRevisionGql = inject(MaterialExplorerCreateTextureSetRevisionGQL)
    readonly route = inject(ActivatedRoute)
    readonly router = inject(Router)
    readonly utils = inject(UtilsService)
    private readonly snackBar = inject(MatSnackBar)
    private readonly dialog = inject(MatDialog)
    private readonly uploadService = inject(UploadGqlService)
    private readonly thumbnailsService = inject(ThumbnailsService)

    constructor() {
        toObservable(this.organizations.$current)
            .pipe(
                filter(IsDefined),
                switchMap((organization) => this.loadData(organization.id)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe()

        this.configMenuService.materialSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_material) => {
            this.$showSizeOptions.set(false)
        })
    }

    async loadData(currentOrganizationId?: string) {
        //TODO: allow the user to specify the organization
        if (!currentOrganizationId) {
            throw new Error("No current organization.")
        }
        const {organization} = await fetchThrowingErrors(this.materialExplorerDataGql)({
            organizationId: currentOrganizationId,
        })
        if (!organization.matExplorerMaterial) {
            throw Error("No material explorer found for this user's organizations.")
        }

        this.organizationId = organization.id
        this.organizationLegacyId = organization.legacyId
        this.$organizationTemplateId.set(organization.matExplorerTemplate?.id)
        this.$materialExplorerItem.set(await fetchThrowingErrors(this.itemGql)({id: organization.matExplorerMaterial.id}).then(({material}) => material))

        const explorerItem = this.$materialExplorerItem()
        if (!explorerItem?.latestRevision) {
            throw Error("Material explorer material does not have a latest revision.")
        }

        this.materialGraph = deserializeMaterialGraph(explorerItem.latestRevision.graphNode)

        this.logger.debug("materialGraph", this.materialGraph)

        const textureNodes = this.materialGraph.nodes.filter((node) => node instanceof TextureFromRevision || node instanceof TextureSet)
        if (textureNodes.length !== 1) {
            throw Error(
                `Material explorer material should have one and only one texture or texture set node. Found the following nodes: ${textureNodes.map((textureNode) => `${textureNode.nodeClass}(${textureNode.id})`)}`,
            )
        }

        const mappingNodes = this.materialGraph.nodes.filter((node) => node instanceof Mapping)
        if (mappingNodes.length !== 1) {
            throw Error("Material explorer material should have one and only one mapping node.")
        }

        if (!explorerItem.textureGroup) {
            throw Error("Material explorer material does not have any texture group.")
        }

        const textureSets = explorerItem.textureGroup.textureSets
        if (textureSets?.length !== 1) {
            throw Error("Material explorer material texture should have one and only one texture set.")
        }

        this.materialRevision = explorerItem.latestRevision
        this.textureSet = textureSets[0]
        if (textureNodes[0] instanceof TextureFromRevision) {
            // old layout
            this.materialLayoutData = {
                type: "OldMaterialLayout",
                texture: this.textureSet.textures.find((texture) => texture?.type === TextureType.Diffuse)!,
                textureRevision: undefined,
                texImageNode: textureNodes[0],
                mappingNode: mappingNodes[0],
            }
        } else {
            // new layout
            this.materialLayoutData = {
                type: "NewMaterialLayout",
                textureSetRevision: undefined,
                imageTextureSetNode: textureNodes[0],
                mappingNode: mappingNodes[0],
            }
        }
    }

    async updateMaterial() {
        switch (this.materialLayoutData.type) {
            case "OldMaterialLayout":
                this.updateOldLayoutNodes(this.materialLayoutData)
                break
            case "NewMaterialLayout":
                this.updateNewLayoutNodes(this.materialLayoutData)
                break
            default:
                throw new Error("Unknown material layout type.")
        }

        this.$configurator().setMaterialGraph(fabricMaterialInputId, this.materialGraph)
    }

    private updateOldLayoutNodes(materialLayoutData: OldMaterialLayoutData) {
        materialLayoutData.texImageNode.updateSetting("textureRevisionLegacyId", materialLayoutData.textureRevision?.legacyId)

        materialLayoutData.mappingNode.parameters = {
            ...materialLayoutData.mappingNode.parameters,
            settings: {
                ...materialLayoutData.mappingNode.parameters.settings,
                rotation: {
                    x: 0,
                    y: 0,
                    z: (this.$parameterRotation() * Math.PI) / 180.0,
                },
                scale: {
                    x: this.measurementTypes.Imperial.convertToMetric(this.$parameterWidth()),
                    y: this.measurementTypes.Imperial.convertToMetric(this.$parameterHeight()),
                    z: 1,
                },
            },
        }
    }

    private updateNewLayoutNodes(materialLayoutData: NewMaterialLayoutData) {
        materialLayoutData.imageTextureSetNode.updateSetting("textureSetRevisionId", materialLayoutData.textureSetRevision!.id)

        materialLayoutData.mappingNode.parameters = {
            ...materialLayoutData.mappingNode.parameters,
            settings: {
                ...materialLayoutData.mappingNode.parameters.settings,
                rotation: {
                    x: 0,
                    y: 0,
                    z: (this.$parameterRotation() * Math.PI) / 180.0,
                },
            },
        }
    }

    async filesSelectedForUpload(event: Event) {
        try {
            const files: FileList = (<HTMLInputElement>event.target).files!
            if (!files.length) return

            if (this.materialLayoutData.type === "OldMaterialLayout") {
                const newTextureRevision = await firstValueFrom(this.uploadNewTextureRevision(files[0], this.materialLayoutData.texture, this.textureSet))
                if (!newTextureRevision) throw new Error("Failed to create a texture revision.")

                await this.discardOldTextureRevision()
                this.materialLayoutData.textureRevision = newTextureRevision
            } else if (this.materialLayoutData.type === "NewMaterialLayout") {
                const newTextureSetRevision = await firstValueFrom(this.uploadNewTextureSetRevision(files[0], this.textureSet))
                if (!newTextureSetRevision) throw new Error("Failed to create a texture set revision.")

                await this.discardOldTextureSetRevision()
                this.materialLayoutData.textureSetRevision = newTextureSetRevision
            } else {
                throw new Error("Unknown material layout type.")
            }
            await this.updateMaterial()
            this.$showSizeOptions.set(true)
            this.loading = false
        } catch (_error: unknown) {
            this.snackBar.open("Cannot upload file.", "", {duration: 3000})
        }
    }

    uploadNewTextureRevision(
        file: File,
        texture: MaterialExplorerTextureFragment,
        _textureSet: MaterialExplorerTextureSetFragment,
    ): Observable<MaterialExplorerTextureRevisionFragment | null> {
        const dialogRef: MatDialogRef<AddTextureDialogComponent, AddTextureDialogResult> = this.dialog.open(AddTextureDialogComponent, {
            width: "350px",
            data: {
                measurementType: this.measurementTypes.Imperial,
                showColorSpaceSetting: false,
            },
        })
        return dialogRef.afterClosed().pipe(
            filter(IsDefined),
            switchMap(async (result: AddTextureDialogResult) => {
                this.loading = true
                const dataObject = await this.uploadService.createAndUploadDataObject(
                    file,
                    {
                        organizationId: this.organizationId!,
                        type: DataObjectType.Texture,
                        imageDataType: TextureTypeLabels.get(texture.type)?.isColorData ? ImageDataType.Color : ImageDataType.NonColor,
                        imageColorSpace: result.colorSpace,
                    },
                    {processUpload: true},
                )
                await this.thumbnailsService.waitUntilAvailable(dataObject.id)
                const textureRevision = await mutateThrowingErrors(this.createTextureRevisionGql)({
                    input: {
                        textureId: texture.id,
                        width: result.width,
                        height: result.height,
                        displacement: result.displacement,
                        dataObjectId: dataObject.id,
                    },
                }).then((result) => result.createTextureRevision)
                this.$parameterWidth.set(this.measurementTypes.Imperial.convertFromMetric(result.width))
                this.$parameterHeight.set(this.measurementTypes.Imperial.convertFromMetric(result.height))
                this.$parameterRotation.set(0)
                return textureRevision
            }),
            takeUntilDestroyed(this.destroyRef),
        )
    }

    async discardOldTextureRevision() {
        if (this.materialLayoutData.type !== "OldMaterialLayout") {
            throw new Error("Material layout is not old.")
        }
        if (!this.materialLayoutData.textureRevision) return

        await mutateThrowingErrors(this.deleteTextureRevisionGql)(this.materialLayoutData.textureRevision)
        this.materialLayoutData.textureRevision = undefined
    }

    uploadNewTextureSetRevision(file: File, textureSet: MaterialExplorerTextureSetFragment): Observable<MaterialExplorerTextureSetRevisionFragment | null> {
        const dialogRef: MatDialogRef<AddTextureDialogComponent, AddTextureDialogResult> = this.dialog.open(AddTextureDialogComponent, {
            width: "350px",
            data: {
                measurementType: this.measurementTypes.Imperial,
                showColorSpaceSetting: false,
            },
        })
        return dialogRef.afterClosed().pipe(
            filter(IsDefined),
            switchMap(async (result: AddTextureDialogResult) => {
                this.loading = true
                const dataObject = await this.uploadService.createAndUploadDataObject(
                    file,
                    {
                        organizationId: this.organizationId!,
                        type: DataObjectType.Texture,
                        imageDataType: ImageDataType.Color,
                        imageColorSpace: result.colorSpace,
                    },
                    {processUpload: true},
                )
                await this.thumbnailsService.waitUntilAvailable(dataObject.id)
                const textureSetRevision = await mutateThrowingErrors(this.createTextureSetRevisionGql)({
                    input: {
                        textureSetId: textureSet.id,
                        width: result.width,
                        height: result.height,
                        displacement: result.displacement,
                        mapAssignments: [
                            {
                                textureType: TextureType.Diffuse,
                                dataObjectId: dataObject.id,
                            },
                        ],
                    },
                }).then((result) => result.createTextureSetRevision)
                this.$parameterWidth.set(this.measurementTypes.Imperial.convertFromMetric(result.width))
                this.$parameterHeight.set(this.measurementTypes.Imperial.convertFromMetric(result.height))
                this.$parameterRotation.set(0)
                return textureSetRevision
            }),
            takeUntilDestroyed(this.destroyRef),
        )
    }

    async discardOldTextureSetRevision() {
        if (this.materialLayoutData.type !== "NewMaterialLayout") {
            throw new Error("Material layout is not new.")
        }
        if (!this.materialLayoutData.textureSetRevision) return

        await mutateThrowingErrors(this.deleteTextureSetRevisionGql)(this.materialLayoutData.textureSetRevision)
        this.materialLayoutData.textureSetRevision = undefined
    }

    updateSize() {
        void this.updateMaterial()
    }

    overlayClosed() {
        void this.router.navigate(["../"], {relativeTo: this.route, queryParamsHandling: "preserve"})
    }

    navigateBack() {
        void this.$routedDialog().closeIfClean()
    }

    ngOnDestroy() {
        void this.discardOldTextureRevision()
    }
}
